/**
 * useSupport Hooks
 *
 * React Query hooks for support ticket functionality.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addMessage,
  createTicket,
  getTicket,
  getTickets,
} from "../services/supportService";
import type {
  AddMessageRequest,
  CreateTicketRequest,
  PaginatedTicketResponse,
  SupportTicketDetail,
  SupportTicketMessage,
} from "../types/support";

/**
 * Hook to fetch the current user's support tickets
 */
export function useTickets(page = 1, pageSize = 20) {
  return useQuery<PaginatedTicketResponse, Error>({
    queryKey: ["support-tickets", page, pageSize],
    queryFn: () => getTickets(page, pageSize),
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook to fetch a specific ticket with messages
 */
export function useTicket(ticketId: string | undefined) {
  return useQuery<SupportTicketDetail, Error>({
    queryKey: ["support-ticket", ticketId],
    queryFn: () => getTicket(ticketId!),
    enabled: !!ticketId,
    staleTime: 10 * 1000, // 10 seconds
  });
}

/**
 * Hook to create a new support ticket
 */
export function useCreateTicket() {
  const queryClient = useQueryClient();

  return useMutation<SupportTicketDetail, Error, CreateTicketRequest>({
    mutationFn: (request) => createTicket(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["support-tickets"] });
    },
  });
}

/**
 * Hook to add a message to a ticket
 */
export function useAddMessage(ticketId: string) {
  const queryClient = useQueryClient();

  return useMutation<SupportTicketMessage, Error, AddMessageRequest>({
    mutationFn: (request) => addMessage(ticketId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["support-ticket", ticketId] });
      queryClient.invalidateQueries({ queryKey: ["support-tickets"] });
    },
  });
}
